import { Helmet } from 'react-helmet';
/**
 * `SummaryQuote` renders a summary of a selected quote/product on the summary
 * or checkout screen.
 *
 * Responsibilities:
 * - Determines the selected variant and renders payment options (single/monthly/
 *   subscription) with appropriate titles and subtitles.
 * - Calculates totals and instalment breakdowns using helpers from
 *   `ProductMethods`.
 * - Displays linked product names when applicable.
 * - Formats start/end dates using the provided `culture`.
 * - Supports edit/remove flows via optional `update` and `remove` callbacks.
 */
import { ProductType, ProductVariant } from '../CommonTypes';
import { PaymentMethod } from '../Constants';
import { PageRelativePath } from '../../../consts/Common';
import { useNavigate } from 'react-router-dom';
import {
  getSelectedVariant,
  getNoOfInstallments,
  getLinkedProductNames,
  getVariantInstalmentTotalPrice,
  calculateTotals,
  getVariantTotalPrice,
  filterRenewalVariants,
  getRenewalExcessData
} from '../../common/ProductMethods';
import { ButtonCommand } from '../../common/Button/ButtonCommand';
import { formatNumber } from '../Formatter';
import { useCurrencySymbol } from '../../../hooks/useCurrencySymbol';
import { CommonValidation } from '../CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';

type SummaryQuoteProps = {
  quote: ProductType;
  error: string;
  policyStartDate: Date;
  isPaymentDone: boolean;
  orderNumbers?: string[];
  bankAuthorisationCode?: string;
  referenceText?: string;
  update?: (
    variantId: string,
    quoteTypeId: number,
    paymentMethod: string
  ) => void;
  remove?: () => void;
  culture: string;
};

const SummaryQuote = ({
  quote,
  error,
  policyStartDate,
  isPaymentDone,
  orderNumbers,
  bankAuthorisationCode,
  update,
  remove,
  referenceText,
  culture
}: SummaryQuoteProps): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const variants = quote.productVariants;

  const buttonRemoveText = t('summary.button-remove-text');
  const buttonEditText = t('summary.button-edit-text');
  const monthlyPayTitle = t('summary.monthly-pay-title');

  const singlePayTitle = t('summary.single-pay-title');
  const subscriptionPlanTitle = t('summary.subscription-plan-title');
  const paymentOptions = t('summary.payment-options');
  const subscriptionRecurringText = t('summary.subscription-recurring-text');

  const noOfInstallments = getNoOfInstallments([quote]);
  const selectedVariant = getSelectedVariant(variants);
  const duration = parseInt(selectedVariant?.duration || variants[0].duration);
  const startDate = new Date(policyStartDate);
  const endDate = new Date(policyStartDate);

  const paymentMethodSortOrder = ['Single', 'Monthly', 'Subscription'];

  endDate.setMonth(endDate.getMonth() + duration);
  endDate.setDate(endDate.getDate() - 1);

  const formattedStartDate = startDate.toLocaleDateString(culture, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const formattedEndDate = endDate.toLocaleDateString(culture, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const currencySymbol = useCurrencySymbol();
  const totals = calculateTotals([quote], noOfInstallments);
  const showEndDate =
    selectedVariant?.paymentMethod === PaymentMethod.Monthly ||
    selectedVariant?.paymentMethod === PaymentMethod.Single ||
    selectedVariant?.paymentMethod === PaymentMethod.PaymentAssist;

  const onChange = (value: string, paymentMethod: string) => {
    if (update) update(value, quote.productTypeId, paymentMethod);
  };

  const renderMonthlyPay = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      quote.productId,
      variant,
      noOfInstallments,
      quote.selectedLinkedProducts ?? [],
      quote.isVariablePrice
    );
    if (isPaymentDone) {
      if (
        installmentPrice.subsequentInstallmentPrice !==
        installmentPrice.firstInstallmentPrice
      ) {
        return t('summary.inequal-monthly-pay', {
          currencySymbol: currencySymbol,
          firstInstallmentPrice: formatNumber(
            installmentPrice.firstInstallmentPrice,
            2,
            culture
          ),
          subsequentInstallmentPrice: formatNumber(
            installmentPrice.subsequentInstallmentPrice,
            2,
            culture
          ),
          noOfInstallments: noOfInstallments - 1
        });
      }

      return t('summary.equal-monthly-pay', {
        currencySymbol: currencySymbol,
        subsequentInstallmentPrice: formatNumber(
          installmentPrice.subsequentInstallmentPrice,
          2,
          culture
        ),
        noOfInstallments: noOfInstallments
      });
    }
    return monthlyPayTitle;
  };

  const renderMonthlyPaySubTitle = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      quote.productId,
      variant,
      noOfInstallments,
      quote.selectedLinkedProducts ?? [],
      quote.isVariablePrice
    );

    if (
      installmentPrice.subsequentInstallmentPrice !==
      installmentPrice.firstInstallmentPrice
    ) {
      return t('summary.inequal-monthly-pay-subtitle', {
        currencySymbol: currencySymbol,
        firstInstallmentPrice: formatNumber(
          installmentPrice.firstInstallmentPrice,
          2,
          culture
        ),
        subsequentInstallmentPrice: formatNumber(
          installmentPrice.subsequentInstallmentPrice,
          2,
          culture
        ),
        noOfInstallments: noOfInstallments - 1
      });
    }
    return t('summary.equal-monthly-pay-subtitle', {
      currencySymbol: currencySymbol,
      subsequentInstallmentPrice: formatNumber(
        installmentPrice.subsequentInstallmentPrice,
        2,
        culture
      ),
      noOfInstallments: noOfInstallments
    });
  };

  const renderMonthlyTotal = (variant: ProductVariant) => {
    const variantTotalPrice = getVariantTotalPrice(
      quote.isVariablePrice,
      variant,
      quote.selectedLinkedProducts
    );

    return `total ${currencySymbol}${formatNumber(variantTotalPrice ?? 0, 2, culture)}`;
  };

  const renderSinglePay = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      quote.productId,
      variant,
      noOfInstallments,
      quote.selectedLinkedProducts ?? [],
      quote.isVariablePrice
    );
    if (isPaymentDone) {
      return t('summary.single-pay-completed', {
        currencySymbol: currencySymbol,
        firstInstallmentPrice: formatNumber(
          installmentPrice.firstInstallmentPrice,
          2,
          culture
        )
      });
    }
    return singlePayTitle;
  };

  const renderSinglePaySubTitle = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      quote.productId,
      variant,
      noOfInstallments,
      quote.selectedLinkedProducts ?? [],
      quote.isVariablePrice
    );

    return t('summary.single-pay-subtitle', {
      currencySymbol: currencySymbol,
      firstInstallmentPrice: formatNumber(
        installmentPrice.firstInstallmentPrice,
        2,
        culture
      )
    });
  };

  const renderSubscriptionPay = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      quote.productId,
      variant,
      noOfInstallments,
      quote.selectedLinkedProducts ?? [],
      quote.isVariablePrice
    );
    if (isPaymentDone) {
      return t('summary.subscription-pay-completed', {
        currencySymbol: currencySymbol,
        firstInstallmentPrice: formatNumber(
          installmentPrice.firstInstallmentPrice,
          2,
          culture
        )
      });
    }
    return subscriptionPlanTitle;
  };

  const renderSubscriptionPaySubTitle = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      quote.productId,
      variant,
      noOfInstallments,
      quote.selectedLinkedProducts ?? [],
      quote.isVariablePrice
    );

    return t('summary.subscription-pay-subtitle', {
      currencySymbol: currencySymbol,
      firstInstallmentPrice: formatNumber(
        installmentPrice.firstInstallmentPrice,
        2,
        culture
      )
    });
  };

  const renderPaymentOptions = () => {
    return filterRenewalVariants(variants, {
      duration: quote.selectedCoverDuration,
      excess: quote.selectedExcess
    })
      .sort(function (a, b) {
        return (
          paymentMethodSortOrder.indexOf(a.paymentMethod) -
          paymentMethodSortOrder.indexOf(b.paymentMethod)
        );
      })
      .map((variant: ProductVariant, i) => {
        return (
          <div key={`payment-${variant.variantId}`}>
            <div className='flex-row m1-top'>
              <label className='inline-flex-style radio-style'>
                <div className='radio-style-div'>
                  <input
                    key={i}
                    type='radio'
                    className='m05-right'
                    value={variant.variantId}
                    onChange={() =>
                      onChange(variant.variantId, variant.paymentMethod)
                    }
                    checked={variant.isSelected === true}
                    id={`select-quote-radio-${i}`}
                  />
                </div>
                {variant.paymentMethod == PaymentMethod.Single &&
                  renderSinglePay(variant)}
                {variant.paymentMethod == PaymentMethod.Monthly &&
                  renderMonthlyPay(variant)}
                {variant.paymentMethod == PaymentMethod.Subscription &&
                  renderSubscriptionPay(variant)}
                {variant.paymentMethod == PaymentMethod.PaymentAssist &&
                  renderMonthlyPay(variant)}
              </label>
            </div>
            <div className='subtitle-style'>
              {variant.paymentMethod == PaymentMethod.Single &&
                renderSinglePaySubTitle(variant)}
              {(variant.paymentMethod == PaymentMethod.Monthly ||
                variant.paymentMethod == PaymentMethod.PaymentAssist) && (
                <>
                  {renderMonthlyPaySubTitle(variant)}
                  <br />
                  {renderMonthlyTotal(variant)}
                </>
              )}
              {variant.paymentMethod == PaymentMethod.Subscription &&
                renderSubscriptionPaySubTitle(variant)}
            </div>
          </div>
        );
      });
  };

  const renderTitle = () => {
    const title = variants[0].customerFriendlyName;

    return title;
  };

  const renderLinkedProductText = () => {
    const result = getLinkedProductNames(
      quote.productId,
      variants[0].linkedProducts,
      quote.selectedLinkedProducts || []
    );

    if (CommonValidation.isNotEmpty(result)) {
      return (
        <p>
          {t('summary.including-products', {
            products: result
          })}
        </p>
      );
    }
  };

  const onNavigateToQuoteEdit = () => {
    navigate(PageRelativePath.Quote);
  };

  const onNavigateToQuoteRemove = () => {
    if (remove) remove();
    navigate(PageRelativePath.Quote);
  };

  const renderPaymentCompleted = () => {
    return variants
      .filter((v) => v.isSelected === true)
      .map((variant: ProductVariant) => {
        return (
          <div key={`payment-${variant.variantId}`}>
            <div className='flex-row m1-top'>
              <label className='inline-flex-style radio-style'>
                {(variant.paymentMethod == PaymentMethod.Monthly ||
                  variant.paymentMethod == PaymentMethod.PaymentAssist) &&
                  renderMonthlyPay(variant)}
                {variant.paymentMethod == PaymentMethod.Single &&
                  renderSinglePay(variant)}
                {variant.paymentMethod === PaymentMethod.Subscription &&
                  renderSubscriptionPay(variant)}
              </label>
            </div>
            {variant.paymentMethod === PaymentMethod.Subscription && (
              <p>{subscriptionRecurringText}</p>
            )}
            {(variant.paymentMethod == PaymentMethod.Single ||
              variant.paymentMethod === PaymentMethod.Subscription) &&
              bankAuthorisationCode && (
                <div className='flex-row m1-top'>
                  {t('summary.bank-authorisation-code', {
                    bankAuthorisationCode: bankAuthorisationCode
                  })}
                </div>
              )}
          </div>
        );
      });
  };

  const renderTotal = () => {
    return (
      <>
        {isPaymentDone &&
          totals &&
          selectedVariant?.paymentMethod !== PaymentMethod.Subscription && (
            <div className='flex-row mobile-to-column m1-top'>
              <p>
                {t('summary.total-text', {
                  currencySymbol: currencySymbol,
                  totalPrice: formatNumber(
                    totals.selectedTotalPrice,
                    2,
                    culture
                  )
                })}
              </p>
            </div>
          )}
      </>
    );
  };

  const renderDuration = () => {
    return (
      <>
        {isPaymentDone &&
          (selectedVariant?.paymentMethod === PaymentMethod.Single ||
            selectedVariant?.paymentMethod === PaymentMethod.Monthly ||
            selectedVariant?.paymentMethod === PaymentMethod.PaymentAssist) && (
            <div className='flex-row mobile-to-column m1-top'>
              <p>
                {t('summary.payment-done-duration', {
                  duration: selectedVariant?.duration
                })}
              </p>
            </div>
          )}
        {!isPaymentDone &&
          (selectedVariant?.paymentMethod === PaymentMethod.Single ||
            selectedVariant?.paymentMethod === PaymentMethod.Monthly ||
            selectedVariant?.paymentMethod === PaymentMethod.PaymentAssist) && (
            <div className='flex-row mobile-to-column m1-top'>
              {t('summary.payment-not-done-duration', {
                duration: selectedVariant?.duration
              })}
            </div>
          )}
      </>
    );
  };

  const renderExcess = () => {
    const { displayExcessOptions } = getRenewalExcessData(
      quote.productVariants,
      currencySymbol
    );
    if (displayExcessOptions) {
      // TODO: consider extract it a separate common component
      // there is a similar code used
      return (
        <div>
          {t('quotes-common.excess')}: {currencySymbol}
          {formatNumber(quote.selectedExcess ?? 0, 2, culture)}
        </div>
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('page-title.checkout')}</title>
      </Helmet>

      <div className='container-item'>
        <div className='flex-row space-between m1-bottom'>
          <div>
            {isPaymentDone && orderNumbers && (
              <div className='flex-row justify-center'>
                <div className='bold m05-top'>{referenceText}</div>
              </div>
            )}
            <div className='flex-row justify-center'>
              <div className='bold m05-top'>{renderTitle()}</div>{' '}
              <p>
                {update && (
                  <ButtonCommand
                    name='btn-edit'
                    text={buttonEditText}
                    additionalClassNames='m05-left small'
                    onClick={onNavigateToQuoteEdit}
                  />
                )}
                {remove && (
                  <ButtonCommand
                    name='btn-remove'
                    text={buttonRemoveText}
                    additionalClassNames='m05-left small'
                    onClick={onNavigateToQuoteRemove}
                  />
                )}
              </p>
            </div>
            {renderLinkedProductText()}
            {renderDuration()}
            {renderExcess()}
            <div className='flex-row mobile-to-column m1-top'>
              <p>
                {t('summary.quote-start-date', {
                  formattedStartDate: formattedStartDate,
                  interpolation: { escapeValue: false }
                })}
              </p>
              {showEndDate === true && (
                <>
                  <p className='m05-left m05-right mobile-hide'>-</p>

                  <p>
                    {t('summary.quote-end-date', {
                      formattedEndDate: formattedEndDate,
                      interpolation: { escapeValue: false }
                    })}
                  </p>
                </>
              )}
            </div>
            {renderTotal()}
          </div>
        </div>
        <hr />
        {!isPaymentDone && <p>{paymentOptions}</p>}
        {isPaymentDone ? renderPaymentCompleted() : renderPaymentOptions()}
        <div className='warning-text m05-bottom'>{error}</div>
      </div>
    </>
  );
};

export default SummaryQuote;
